import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { useSnackbar } from '../common/Snackbar';
import { resolveDefaultRegion, sanitizeRegionValue, validateEscalationForm, buildEscalationPayload } from '../../utils/escalateQueryUtils';
import { useEscalateQueryState } from '../../hooks/queries/escalateQuery/useEscalateQueryState';
import { useEscalateQueryData } from '../../hooks/queries/escalateQuery/useEscalateQueryData';
import { useEscalateQueryCalculations } from '../../hooks/queries/escalateQuery/useEscalateQueryCalculations';
import {
  dialogPaperStyles,
  dialogContentStyles,
  dialogActionsStyles,
  cancelButtonStyles,
  submitButtonStyles,
  submitSpinnerStyles,
  CIRCULAR_PROGRESS_SIZES,
} from '../../constants/escalateQueryDialog';
import EscalateQueryDialogHeader from './escalateQueryDialog/EscalateQueryDialogHeader';
import LoadingDisplay from './escalateQueryDialog/LoadingDisplay';
import ErrorDisplay from './escalateQueryDialog/ErrorDisplay';
import EmptyStateDisplay from './escalateQueryDialog/EmptyStateDisplay';
import EscalationForm from './escalateQueryDialog/EscalationForm';

const EscalateQueryDialog = ({
  open,
  onClose,
  onSubmit,
  query,
  submitting = false,
}) => {
  const { showSnackbar } = useSnackbar();
  const state = useEscalateQueryState();
  const {
    currentUser,
    region,
    targets,
    allowedRoles,
    loadingOptions,
    loadError,
    selectedRole,
    selectedUserId,
    errors,
    setSelectedRole,
    setSelectedUserId,
    setErrors,
    resetState,
  } = state;

  const { fetchCurrentUser, fetchEscalationOptions } = useEscalateQueryData(
    query,
    region,
    state
  );

  const { fields, usersById, roleOptions } = useEscalateQueryCalculations(
    currentUser,
    allowedRoles,
    targets,
    selectedRole,
    selectedUserId,
    state
  );

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    fetchCurrentUser();
  }, [open, fetchCurrentUser, resetState]);

  useEffect(() => {
    if (!open) return;
    const defaultRegion = resolveDefaultRegion(currentUser, query);
    const sanitized = sanitizeRegionValue(defaultRegion);
    state.setRegion(sanitized);
    fetchEscalationOptions(sanitized);
  }, [open, currentUser, query, fetchEscalationOptions, state]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleRoleChange = (event) => {
    const nextRole = event.target.value;
    setSelectedRole(nextRole);
    setSelectedUserId('');
    setErrors((prev) => ({
      ...prev,
      role: undefined,
      target: undefined,
    }));
  };

  const handleUserChange = (event) => {
    setSelectedUserId(event.target.value);
    setErrors((prev) => ({ ...prev, target: undefined }));
  };

  const handleSubmit = () => {
    const validation = validateEscalationForm(selectedRole, selectedUserId, fields);

    if (fields.length === 0) {
      showSnackbar('Escalation is not available for your role', 'error');
      return;
    }

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    const payload = buildEscalationPayload(selectedUserId, usersById, selectedRole, region);

    if (!payload) {
      showSnackbar('Unable to resolve the selected escalation target.', 'error');
      return;
    }

    onSubmit?.(payload);
  };

  const renderContent = () => {
    if (loadError) {
      return <ErrorDisplay loadError={loadError} />;
    }

    if (!fields.length) {
      return <EmptyStateDisplay />;
    }

    return (
      <EscalationForm
        roleOptions={roleOptions}
        selectedRole={selectedRole}
        selectedUserId={selectedUserId}
        errors={errors}
        onRoleChange={handleRoleChange}
        onUserChange={handleUserChange}
      />
    );
  };

  const hasSelectedTarget = !loadError && fields.length > 0 && Boolean(selectedRole && selectedUserId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperStyles }}
    >
      <EscalateQueryDialogHeader />

      <DialogContent sx={dialogContentStyles}>
        {loadingOptions ? <LoadingDisplay /> : renderContent()}
      </DialogContent>

      <DialogActions sx={dialogActionsStyles}>
        <Button onClick={handleClose} variant="outlined" disabled={submitting} sx={cancelButtonStyles}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loadingOptions || !!loadError || !fields.length || !hasSelectedTarget}
          sx={submitButtonStyles}
        >
          {submitting ? (
            <CircularProgress size={CIRCULAR_PROGRESS_SIZES.SUBMIT} sx={submitSpinnerStyles} />
          ) : (
            'Confirm Escalation'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EscalateQueryDialog;
